import {Loader} from 'pixi.js';
import {TemplateUtils} from '@gongxigames/framework/slot/services/TemplateUtils';
import {HTMLSystemPopup, SystemPopup} from '@gongxigames/framework/slot/types';
import {IBrowser, ICurrencyFormatter, LanguageService} from '@gongxigames/framework/core';
import {dateDiffInSeconds, formatDuration} from '@gongxigames/framework/slot/utils/TimeUtils';
import {PromotionBalanceEntryQualifier, PromotionData} from '@gongxigames/framework/promotions';

type PopupButtonParameters = {
    onClick: () => void;
    textID: string;
    classList?: string[];
};

type PopupParameters = {
    titleTextID: string;
    contentTextID: string;
    contentTextValues?: Record<string, any>;
    id?: string;
    closeButtonHidden?: boolean;
    promotionInfoContent?: string;
    onClickPromotionInfo?: () => void;
    contentBeforeButtons?: string;
    buttons: PopupButtonParameters[];
    buttonsClassList?: string[];
    popupClassList?: string[];
    contentAfterButtons?: string;
    promotionType?: PromotionBalanceEntryQualifier;
};

export type GameSessionPromotionEndPopupParameters = {
    onClickContinue: () => void;
    onClickExit: () => void;
} & PromotionData;

export type GameSessionPromotionStartPopupParameters = {
    onClickActionButton: () => void;
} & PromotionData;

export type SingleSetPromotionStartPopupParameters = {
    onClickAccept: () => void;
    onClickAskMeLater: () => void;
} & PromotionData;

export type MultiSetsPromotionStartPopupParameters = {
    totalBet: number;
    onClickAccept: () => void;
    onClickAskMeLater: () => void;
} & PromotionData;

export type PromotionRegulationSettings = {
    valuedAtEnabled: boolean;
    expiresInEnabled: boolean;
    optOutEnabled: boolean;
    playLaterEnabled: boolean;
    youHaveWonEnabled: boolean;
};

export type MultiSetsMultiCampaignPromotionStartPopupParameters = {
    totalBet: number;
    onClickAccept: () => void;
    onClickPlayLater: () => void;
    onClickNoThanks: () => void;
    onExpiration?: () => void;
    onClickPromotionInfo?: () => void;
    regulationSettings: PromotionRegulationSettings;
} & PromotionData;

export type MultiSetsMultiCampaignPromotionOptOutPopupParameters = {
    onClickYes: () => void;
    onClickNo: () => void;
    onExpiration?: () => void;
    regulationSettings: PromotionRegulationSettings;
} & PromotionData;

export type MultiSetsMultiCampaignPromotionInfoPopupParameters = {
    onOk: () => void;
} & PromotionData;

export type MultiSetsMultiCampaignPromotionWhatIsAFPopupParameters = {
    onOk: () => void;
} & PromotionData;

export type MultiSetsMultiCampaignPromotionContinuePopupParameters = {
    totalBet: number;
    onClickKeepGoing: () => void;
    onExpiration?: () => void;
    regulationSettings: PromotionRegulationSettings;
} & PromotionData;

export type MultiSetsPromotionContinuePopupParameters = {
    totalBet: number;
    onClickKeepGoing: () => void;
} & PromotionData;

export type MultiSetsPromotionEndPopupParameters = {
    totalBet: number;
    onClickOk: () => void;
} & PromotionData;

export type MultiSetsMultiCampaignPromotionEndPopupParameters = {
    totalBet: number;
    onClickOk: () => void;
    regulationSettings: PromotionRegulationSettings;
} & PromotionData;

export class PromotionPopupFactory {
    private _timeoutID = -1;

    constructor(private language: LanguageService, private currencyFormatter: ICurrencyFormatter, private browser: IBrowser) {}

    public createGameSessionPromotionStartPopup({
        id,
        total,
        onClickActionButton,
        type,
    }: GameSessionPromotionStartPopupParameters): SystemPopup {
        const promotionPrefix = this.getPluralOrSingularPromotionPrefix(type, total);
        const contentTextID = promotionPrefix + 'RubyWelcomeDesc';
        return this.createPopup({
            id,
            titleTextID: 'promotionStartTitle',
            contentTextID: contentTextID,
            contentTextValues: {n: total},
            buttons: [{onClick: onClickActionButton, textID: 'clickToStart', classList: ['accent']}],
            promotionType: 'free-round'
        });
    }

    public createGameSessionPromotionContinuePopup({
        id,
        left,
        total,
        onClickActionButton,
        type,
    }: GameSessionPromotionStartPopupParameters): SystemPopup {
        const promotionPrefix = this.getPluralOrSingularPromotionPrefix(type, left);
        const contentTextID = promotionPrefix + 'RubyLeft';
        return this.createPopup({
            id,
            titleTextID: 'welcomeBack',
            contentTextID: contentTextID,
            contentTextValues: {left, total},
            buttons: [{onClick: onClickActionButton, textID: 'clickToContinue', classList: ['accent']}],
            promotionType: 'free-round'
        });
    }

    public createGameSessionPromotionEndPopup({
        id,
        win,
        onClickContinue,
        onClickExit,
    }: GameSessionPromotionEndPopupParameters): SystemPopup {
        const isWin = win > 0;

        return this.createPopup({
            id,
            titleTextID: isWin ? 'freeRoundsSummaryTitle' : 'freeRoundsSummaryTitleLose',
            contentTextID: isWin ? 'freeRoundsSummaryDesc' : 'freeRoundsSummaryTitleLose',
            contentTextValues: isWin ? { win: this.currencyFormatter.formatCents(win) } : undefined,
            buttons: [
                { textID: 'clickToContinue', onClick: onClickContinue, classList: ['accent'] },
                { textID: 'exit', onClick: onClickExit },
            ],
            promotionType: 'free-round',
        });
    }

    public createSingleSetStartPopup(parameters: SingleSetPromotionStartPopupParameters): SystemPopup {
        const {id, total, onClickAccept, onClickAskMeLater, type} = parameters;
        const promotionPrefix = this.getPluralOrSingularPromotionPrefix(type, total);
        const contentTextID = promotionPrefix + 'RubyWelcomeDesc';

        return this.createPopup({
            id,
            titleTextID: 'promotionStartTitle',
            contentTextID: contentTextID,
            contentTextValues: {n: total},
            buttons: [
                { onClick: onClickAccept, textID: 'playNow', classList: ['accent'] },
                { onClick: onClickAskMeLater, textID: 'askMeLater' },
            ],
            promotionType: 'free-round',
        });
    }

    public createMultiSetsMulticampaignStartPopup(parameters: MultiSetsMultiCampaignPromotionStartPopupParameters): SystemPopup {
        const {
            left,
            total,
            totalBet,
            totalPrice,
            expirationTime,
            rulesUrl,
            started,
            onClickAccept,
            onClickPlayLater,
            onClickNoThanks,
            onExpiration,
            regulationSettings: { valuedAtEnabled, expiresInEnabled, optOutEnabled, playLaterEnabled},
            type,
            onClickPromotionInfo,
        } = parameters;
        const showExpiration = !!(expiresInEnabled && expirationTime);
        const promotionPrefix = this.getPluralOrSingularPromotionPrefix(type, total);
        const bet = this.currencyFormatter.formatCents(this.isFreeRoundsPromotion(type) ? totalBet : totalPrice!);
        const contentTextValues = {n: left, left: left, bet};
        const welcomeTextID = valuedAtEnabled ? `${promotionPrefix}RubyWelcomeDescValuedAt`: `${promotionPrefix}RubyWelcomeDesc`;
        const leftTextID = valuedAtEnabled ? `${promotionPrefix}RubyLeftValuedAt`: `${promotionPrefix}RubyLeft`;
        const contentTextID = started ? leftTextID : welcomeTextID;
        const buttonsClassList = [];
        const promotionInfoContent = started ? '' : this.getPromotionInfoHTML(type);
        const contentBeforeButtons = this.getExpireInHTML(showExpiration, type);
        const shouldShowInfoIcon = type === 'awarded-feature' && !started;
        let contentAfterButtons = '';

        if (rulesUrl) {
            contentAfterButtons += `<p class="rules-url">${this.language.get('promotionRubyRules', { rulesUrl })}</p>`;
        }
        
        const onClickAcceptWrapper = () => {
            const playNowButtonElement = document.querySelector('.promotion-start button.accent');
            const buttonElements = document.querySelectorAll('.promotion-start button');
            buttonElements.forEach((button) => {
                button.setAttribute('disabled', 'true');
            });
            document.querySelector('.promotion-start .rules-url a')?.classList.add('disabled');
            playNowButtonElement?.classList.add('fetching');
            onClickAccept();
        }

        const buttons: PopupButtonParameters[] = [{ onClick: onClickAcceptWrapper, textID: 'playNow', classList: ['accent'] }];

        if (optOutEnabled) {
            buttons.push({ onClick: onClickNoThanks, textID: 'noThanks' });
        }

        if (playLaterEnabled) {
            buttons.push({ onClick: onClickPlayLater, textID: 'playLater' });
        }

        if (buttons.length > 2) {
            buttonsClassList.push('long-short-buttons');
        }


        const popup = this.createPopup({
            titleTextID: 'promotionStartTitle',
            contentTextID,
            contentTextValues,
            promotionInfoContent,
            contentBeforeButtons,
            contentAfterButtons,
            buttonsClassList,
            popupClassList: ['promotion-start'],
            buttons,
            promotionType: type,
            ...(shouldShowInfoIcon ? { onClickPromotionInfo } : {})
        });

        this.addExpirationTimer(type, expirationTime!, onExpiration);

        return popup;
    }

    public createMultiSetsMulticampaignOptOutPopup(params: MultiSetsMultiCampaignPromotionOptOutPopupParameters): SystemPopup {
        const {
            total,
            onClickYes,
            onClickNo,
            expirationTime,
            onExpiration,
            regulationSettings: { expiresInEnabled },
            type,
        } = params;
        const showExpiration = !!(expiresInEnabled && expirationTime);
        const promotionPrefix = this.getPluralOrSingularPromotionPrefix(type, total);
        const contentBeforeButtons = this.getExpireInHTML(showExpiration, type);

        const contentTextID = promotionPrefix + 'NotParticipate';

        const popup = this.createPopup({
            titleTextID: 'promotionAreYouSure',
            contentTextID,
            contentBeforeButtons,
            popupClassList: ['without-background-title', 'promotion-opt-out'],
            buttonsClassList: ['short-buttons'],
            buttons: [
                { textID: 'promotionNoText', onClick: onClickNo, classList: ['accent'] },
                { textID: 'promotionYesText', onClick: onClickYes, classList: ['accent'] },
            ],
            promotionType: type,
        });

        this.addExpirationTimer(type, expirationTime!, onExpiration);

        return popup;
    }

    public createMultiSetsMulticampaignInfo(params: MultiSetsMultiCampaignPromotionInfoPopupParameters): SystemPopup {
        const { onOk } = params;

        return this.createPopup({
            titleTextID: 'promotionInfo',
            contentTextID: 'promotionNotAvailable',
            popupClassList: ['promotion-info'],
            buttonsClassList: ['long-buttons'],
            buttons: [{ textID: 'ok', onClick: onOk }],
        });
    }

    public createMultiSetsMulticampaignPromotionEndPopup(params: MultiSetsMultiCampaignPromotionEndPopupParameters): SystemPopup {
        const {
            totalBet,
            totalPrice,
            total,
            win,
            type,
            regulationSettings: { youHaveWonEnabled },
        } = params;
        const bet = this.currencyFormatter.formatCents(this.isFreeRoundsPromotion(type) ? totalBet : totalPrice!);
        const formattedWin = this.currencyFormatter.formatCents(win);
        const contentTextID = win > 0 && youHaveWonEnabled ? 'promotionYouFinishedSimple' : '';
        const title = type === 'free-round' ? 'relaxFreeRoundsEndTitle' : 'awardedFeaturesEndTitle';

        return this.createPopup({
            titleTextID: title,
            contentTextID,
            popupClassList: ['promotion-end', 'promotion-capitalize-title'],
            contentTextValues: {
                amount: total,
                totalAmount: total,
                win: formattedWin,
                bet,
            },
            buttons: [{ onClick: params.onClickOk, textID: 'ok', classList: ['accent'] }],
            promotionType: type,
        });
    }

    public createMultiSetsMulticampaignPromotionContinuePopup(params: MultiSetsMultiCampaignPromotionContinuePopupParameters): SystemPopup {
        const {
            total,
            left,
            totalBet,
            totalPrice,
            expirationTime,
            onExpiration,
            regulationSettings: { valuedAtEnabled, expiresInEnabled },
            type,
        } = params;
        const bet = this.currencyFormatter.formatCents(this.isFreeRoundsPromotion(type) ? totalBet : totalPrice!);
        const titleTextID = type === 'free-round' ? 'relaxFreeRoundsContinueTitle' : 'awardedFeatureContinueTitle';
        const promotionPrefix = this.getPluralOrSingularPromotionPrefix(type, left);
        const contentTextManyRoundsID = valuedAtEnabled ? `${promotionPrefix}RubyLeftValuedAt`: `${promotionPrefix}RubyLeft`;
        const contentTextSingleRoundID = valuedAtEnabled ? `${promotionPrefix}RubyLeftLastValuedAt`: `${promotionPrefix}RubyLeftLast`;
        const contentTextID = left === 0 ? contentTextSingleRoundID : contentTextManyRoundsID;

        const showExpiration = !!(expiresInEnabled && expirationTime);
        const contentBeforeButtons = this.getExpireInHTML(showExpiration, type);

        const popup = this.createPopup({
            titleTextID,
            contentTextID,
            contentTextValues: {
                left,
                total,
                bet
            },
            contentBeforeButtons,
            popupClassList: ['promotion-continue'],
            buttons: [{ onClick: params.onClickKeepGoing, textID: 'keepGoing', classList: ['accent'] }],
            promotionType: type,
        });

        this.addExpirationTimer(type, expirationTime!, onExpiration);

        return popup;
    }

    public createMultiSetsMulticampaignPromotionWhatIsAFPopup(params: MultiSetsMultiCampaignPromotionWhatIsAFPopupParameters): SystemPopup {
        return this.createPopup({
            titleTextID: 'whatIsAwardedFeaturePromoInfoTitle',
            contentTextID: 'whatIsAwardedFeaturePromoInfoText',
            popupClassList: ['without-background-title', 'promotion-what-is-af-popup'],
            buttonsClassList: ['long-buttons'],
            buttons: [{ textID: 'gotIt', onClick: params.onOk, classList: ['accent'] }],
        });
    }

    public createMultiSetsPromotionStartPopup(parameters: MultiSetsPromotionStartPopupParameters): SystemPopup {
        const { total, onClickAccept, onClickAskMeLater, totalBet, type } = parameters;
        const bet = this.currencyFormatter.formatCents(totalBet);
        const promotionPrefix = this.getPluralOrSingularPromotionPrefix(type, total);
        const contentTextID = promotionPrefix + 'RubyWelcomeDescValuedAt';

        return this.createPopup({
            titleTextID: 'promotionStartTitle',
            contentTextID: contentTextID,
            contentTextValues: {n: total, bet},
            buttons: [
                { onClick: onClickAccept, textID: 'playNow', classList: ['accent'] },
                { onClick: onClickAskMeLater, textID: 'askMeLater' },
            ],
        });
    }

    public createMultiSetsPromotionContinuePopup(params: MultiSetsPromotionContinuePopupParameters): SystemPopup {
        const bet = this.currencyFormatter.formatCents(params.totalBet);
        const promotionPrefix = this.getPluralOrSingularPromotionPrefix(params.type, params.left);
        const contentTextID = promotionPrefix + 'RubyLeftValuedAt';

        return this.createPopup({
            titleTextID: 'relaxFreeRoundsContinueTitle',
            contentTextID: contentTextID,
            contentTextValues: {
                left: params.left,
                total: params.total,
                bet,
            },
            popupClassList: [],
            buttons: [{ onClick: params.onClickKeepGoing, textID: 'keepGoing', classList: ['accent'] }],
        });
    }

    public createMultiSetsPromotionEndPopup(params: MultiSetsPromotionEndPopupParameters): SystemPopup {
        const { totalBet, total, win, type } = params;
        const bet = this.currencyFormatter.formatCents(totalBet);
        const formattedWin = this.currencyFormatter.formatCents(win);
        const promotionPrefix = this.getPluralOrSingularPromotionPrefix(type, total);
        const titleTextID = 'relaxFreeRoundsEndTitle';

        const contentTextID = total === 0 
                ? `${promotionPrefix}YouFinishedValuedAtAndWon` 
                : win > 0 
                    ? `${promotionPrefix}YouFinishedValuedAtAndWon`
                    : `${promotionPrefix}YouFinishedValuedAt`;

        return this.createPopup({
            titleTextID,
            contentTextID,
            contentTextValues: {
                amount: total,
                totalAmount: total,
                win: formattedWin,
                bet,
            },
            popupClassList: [],
            buttons: [{ onClick: params.onClickOk, textID: 'ok', classList: ['accent'] }],
        });
    }

    public clearExpirationTimeout(): void {
        this.browser.removeTimeout(this._timeoutID);
    }

    private createPopup(parameters: PopupParameters): HTMLSystemPopup {
        const {
            id,
            titleTextID,
            contentTextID,
            contentTextValues,
            promotionInfoContent,
            buttons,
            contentAfterButtons,
            contentBeforeButtons,
            buttonsClassList,
            popupClassList,
            promotionType,
            onClickPromotionInfo,
        } = parameters;

        const template = Loader.shared.resources.promotionPopup.data;
        const templateId = 'promotion-start';
        const popupId = 'promotion-popup';
        const popupClass = 'promotion';
        const popupButtons = 'promotion-buttons';

        const popup = TemplateUtils.createPopup(templateId, this.language.get(titleTextID), template);
        const contentText = contentTextID === '' ? '' : this.language.get(contentTextID, contentTextValues);
        const buttonsDiv = '<div class="' + popupButtons + '"></div>';

        popup.content.innerHTML = `${contentText}${promotionInfoContent ?? ''}${contentBeforeButtons ?? ''}${
            id ? this.getVoucherIdContent(id) : ''
        }${buttonsDiv}${contentAfterButtons ?? ''}`;
        popup.popup.classList.add(popupClass);
        popup.popup.classList.add(...(popupClassList ?? []));
        popup.content.classList.add(...(buttonsClassList?.length ? buttonsClassList : ['long-buttons']));
        popup.closeButton.hidden = true;

        const infoIcon = popup.content.querySelector('.awarded-info-icon') as HTMLButtonElement;
        if (infoIcon){
            if(onClickPromotionInfo) {
                infoIcon.onclick = onClickPromotionInfo;
            } else {
                infoIcon.remove();
            }
        }

        buttons.forEach(({ textID, onClick, classList }) => {
            const button = document.createElement('button');

            if (classList) {
                button.classList.add(...classList);
            }

            button.textContent = this.language.get(textID);
            button.onclick = onClick;
            popup.content.getElementsByClassName(popupButtons)[0].insertAdjacentElement('beforeend', button);
        });

        return {
            id: popupId,
            priority: popup.priority,
            view: popup,
            type: 'HTML',
        };
    }

    private getVoucherIdContent(id: string): string {
        const voucherIdText = this.language.get('freeRoundVoucher', { id });

        return `<p class="voucherID">${voucherIdText}</p>`;
    }

    private getFormatedTime(expirationTime: string): string {
        const difference = dateDiffInSeconds(Date.now(), new Date(`${expirationTime}Z`).getTime());

        if (difference < 0) {
            return '';
        }

        return formatDuration(difference, this.language);
    }

    private addExpirationTimeout(timer: HTMLElement, expirationTime: string, onExpirationTime: (() => void) | undefined): void {
        let cachedTime = '';
        const tick = () => {
            const time = this.getFormatedTime(expirationTime);

            if (!time) {
                this.clearExpirationTimeout();
                onExpirationTime?.();

                return;
            }

            if (timer && time !== cachedTime) {
                timer.innerHTML = this.language.get('promotionRubyExpiresIn', { time });
                cachedTime = time;
            }

            this._timeoutID = this.browser.addTimeout(tick, 1000);
        };

        tick();
    }

    private getExpireInHTML(showExpiration: boolean, type: PromotionBalanceEntryQualifier): string {
        const expiryClass = type === 'free-round' ? 'free-rounds-expire-in' : 'promotion-expire-in';

        if (showExpiration) {
            return `<p class="expire-in" id="${expiryClass}"></p>`;
        } else {
            return `<p style="display: none" id="${expiryClass}"></p>`;
        }
    }

    private getPluralOrSingularPromotionPrefix(type: PromotionBalanceEntryQualifier, amountOfPromotions: number): string {
        const isSingular = amountOfPromotions <= 1;
        const promotionTypeTextId = 
            type === 'free-round' 
            ? isSingular 
                ? 'freeRound' 
                : 'freeRounds' 
            : isSingular 
                ? 'awardedFeature' 
                : 'awardedFeatures';

        return promotionTypeTextId;
    }

    private getPromotionInfoHTML(type: PromotionBalanceEntryQualifier): string {
        if (type === 'awarded-feature') {
            return '<div class="feature-info">' + this.language.get('whatIsAwardedFeaturePromoInfo') + '</div>';
        }

        return '';
    }

    private addExpirationTimer(type: PromotionBalanceEntryQualifier, expirationTime: string, onExpiration: (() => void) | undefined): void {
        const timerId = type === 'free-round' ? 'free-rounds-expire-in' : 'promotion-expire-in';
        const timer = document.getElementById(timerId);

        timer && this.addExpirationTimeout(timer, expirationTime, onExpiration);
    }

    private isFreeRoundsPromotion(type: PromotionBalanceEntryQualifier): boolean {
        return type === 'free-round';
    }
}
